import { React, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSlug from 'rehype-slug'
import Fuse from 'fuse.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons'

import './MarkdownDoc.css'

function buildSearchEntries(root) {
    const headings = Array.from(root.querySelectorAll('h2[id], h3[id]'))
    return headings.map((heading, index) => {
        const parts = [heading.textContent || '']
        let node = heading.nextElementSibling
        const nextHeading = headings[index + 1] || null
        while (node && node !== nextHeading) {
            if (!node.matches('h1, h2, h3')) {
                const chunk = (node.textContent || '').replace(/\s+/g, ' ').trim()
                if (chunk) parts.push(chunk)
            }
            node = node.nextElementSibling
        }
        const title = (heading.textContent || '').trim()
        const body = parts.slice(1).join(' ').slice(0, 1200)
        return {
            id: heading.id,
            title,
            body,
            level: heading.tagName === 'H3' ? 3 : 2,
            preview: body.slice(0, 140) + (body.length > 140 ? '…' : ''),
        }
    }).filter((entry) => entry.id && entry.title)
}

/**
 * Lädt Markdown (URL oder content) und rendert es inkl. GFM, Ankern,
 * optionaler sticky Inhaltsverzeichnis-Seitenleiste und Fuzzy-Suche.
 */
export default function MarkdownDoc({
    src,
    content: contentProp,
    imageBase,
    className = '',
    showToc = false,
    tocTitle = 'Inhalt',
    showSearch = false,
    searchPlaceholder = 'Suchen… z. B. Passwort, Absage, Warteliste',
}) {
    const [content, setContent] = useState(contentProp || '')
    const [status, setStatus] = useState(contentProp ? 'ready' : (src ? 'loading' : 'empty'))
    const [error, setError] = useState('')
    const [toc, setToc] = useState([])
    const [searchIndex, setSearchIndex] = useState([])
    const [activeId, setActiveId] = useState('')
    const [tocOpen, setTocOpen] = useState(false)
    const [query, setQuery] = useState('')
    const articleRef = useRef(null)
    const searchInputRef = useRef(null)

    const resolvedImageBase = useMemo(() => {
        if (imageBase) return imageBase.replace(/\/?$/, '/')
        if (!src) return '/'
        const withoutFile = src.replace(/[^/]+$/, '')
        return withoutFile.endsWith('/') ? withoutFile : withoutFile + '/'
    }, [src, imageBase])

    const fuse = useMemo(() => {
        if (!searchIndex.length) return null
        return new Fuse(searchIndex, {
            keys: [
                { name: 'title', weight: 0.7 },
                { name: 'body', weight: 0.3 },
            ],
            threshold: 0.42,
            ignoreLocation: true,
            includeScore: true,
            minMatchCharLength: 2,
        })
    }, [searchIndex])

    const searchResults = useMemo(() => {
        const q = query.trim()
        if (!fuse || q.length < 2) return []
        return fuse.search(q).slice(0, 8).map((hit) => hit.item)
    }, [fuse, query])

    useEffect(() => {
        if (contentProp != null && contentProp !== '') {
            setContent(contentProp)
            setStatus('ready')
            setError('')
            return
        }
        if (!src) {
            setStatus('empty')
            return
        }

        let cancelled = false
        setStatus('loading')
        setError('')

        fetch(src)
            .then((res) => {
                if (!res.ok) throw new Error('Dokument konnte nicht geladen werden (' + res.status + ').')
                return res.text()
            })
            .then((text) => {
                if (cancelled) return
                setContent(text)
                setStatus('ready')
            })
            .catch((err) => {
                if (cancelled) return
                setError(err.message || String(err))
                setStatus('error')
            })

        return () => { cancelled = true }
    }, [src, contentProp])

    useEffect(() => {
        if (status !== 'ready' || !articleRef.current) {
            setToc([])
            setSearchIndex([])
            return
        }

        let cancelled = false
        let observer

        const frame = requestAnimationFrame(() => {
            if (cancelled || !articleRef.current) return

            const root = articleRef.current
            const entries = buildSearchEntries(root)
            setSearchIndex(entries)
            if (showToc) {
                setToc(entries.map(({ id, title, level }) => ({ id, text: title, level })))
            } else {
                setToc([])
            }

            const hash = (window.location.hash || '').replace(/^#/, '')
            if (hash) {
                const target = root.querySelector('#' + CSS.escape(hash)) || document.getElementById(hash)
                if (target) {
                    setActiveId(hash)
                    setTimeout(() => {
                        if (!cancelled) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }, 50)
                }
            }

            if (!showToc || !entries.length || typeof IntersectionObserver === 'undefined') return

            const nodes = root.querySelectorAll('h2[id], h3[id]')
            observer = new IntersectionObserver(
                (obsEntries) => {
                    const visible = obsEntries
                        .filter((entry) => entry.isIntersecting)
                        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
                    if (visible[0]?.target?.id) {
                        setActiveId(visible[0].target.id)
                    }
                },
                { rootMargin: '-20% 0px -65% 0px', threshold: [0, 1] }
            )

            nodes.forEach((node) => observer.observe(node))
        })

        return () => {
            cancelled = true
            cancelAnimationFrame(frame)
            if (observer) observer.disconnect()
        }
    }, [showToc, status, content])

    function resolveAssetUrl(url) {
        if (!url) return url
        if (/^(https?:|data:|mailto:|#|\/)/i.test(url)) return url
        return resolvedImageBase + url.replace(/^\.\//, '')
    }

    function scrollToHeading(id) {
        const el = articleRef.current?.querySelector('#' + CSS.escape(id))
            || document.getElementById(id)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setActiveId(id)
        setTocOpen(false)
        if (window.history?.replaceState) {
            window.history.replaceState(null, '', '#' + id)
        }
    }

    function clearSearch() {
        setQuery('')
        searchInputRef.current?.focus()
    }

    if (status === 'loading') {
        return (
            <div className={'markdown-doc markdown-doc--loading ' + className} role="status">
                Dokumentation wird geladen…
            </div>
        )
    }

    if (status === 'error') {
        return (
            <div className={'markdown-doc markdown-doc--error ' + className} role="alert">
                {error || 'Dokumentation konnte nicht geladen werden.'}
            </div>
        )
    }

    if (status === 'empty' || !content) {
        return null
    }

    const article = (
        <article ref={articleRef} className={'markdown-doc ' + className}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSlug]}
                components={{
                    img({ src: imgSrc, alt, ...rest }) {
                        return (
                            <img
                                src={resolveAssetUrl(imgSrc)}
                                alt={alt || ''}
                                loading="lazy"
                                className="markdown-doc__img"
                                {...rest}
                            />
                        )
                    },
                    a({ href, children, ...rest }) {
                        const resolved = resolveAssetUrl(href)
                        const external = /^https?:/i.test(resolved || '')
                        return (
                            <a
                                href={resolved}
                                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                {...rest}
                            >
                                {children}
                            </a>
                        )
                    },
                    table({ children }) {
                        return (
                            <div className="markdown-doc__table-wrap">
                                <table>{children}</table>
                            </div>
                        )
                    },
                }}
            >
                {content}
            </ReactMarkdown>
        </article>
    )

    const searchUi = showSearch ? (
        <div className="markdown-doc-search">
            <label className="markdown-doc-search__label" htmlFor="markdown-doc-search-input">
                Handbuch durchsuchen
            </label>
            <div className="markdown-doc-search__row">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="markdown-doc-search__icon" aria-hidden="true" />
                <input
                    id="markdown-doc-search-input"
                    ref={searchInputRef}
                    type="search"
                    className="markdown-doc-search__input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    autoComplete="off"
                    enterKeyHint="search"
                />
                {query ? (
                    <button type="button" className="markdown-doc-search__clear" onClick={clearSearch} aria-label="Suche leeren">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                ) : null}
            </div>
            {query.trim().length >= 2 && (
                <div className="markdown-doc-search__results" role="listbox" aria-label="Suchergebnisse">
                    {searchResults.length === 0 ? (
                        <p className="markdown-doc-search__empty">Keine Treffer. Versuch ein anderes Wort (z. B. „Passwort“ oder „Absage“).</p>
                    ) : (
                        <ul className="markdown-doc-search__list">
                            {searchResults.map((item) => (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        className="markdown-doc-search__hit"
                                        onClick={() => scrollToHeading(item.id)}
                                    >
                                        <span className="markdown-doc-search__hit-title">{item.title}</span>
                                        {item.preview ? (
                                            <span className="markdown-doc-search__hit-preview">{item.preview}</span>
                                        ) : null}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    ) : null

    if (!showToc && !showSearch) {
        return article
    }

    if (!showToc) {
        return (
            <div className="markdown-doc-layout">
                {searchUi}
                <div className="markdown-doc-main">{article}</div>
            </div>
        )
    }

    return (
        <div className={'markdown-doc-layout' + (toc.length ? ' markdown-doc-layout--with-toc' : '')}>
            {searchUi}
            <div className="markdown-doc-main">
                {article}
            </div>
            {toc.length > 0 && (
                <nav className={'markdown-doc-toc' + (tocOpen ? ' markdown-doc-toc--open' : '')} aria-label="Inhaltsverzeichnis">
                    <button
                        type="button"
                        className="markdown-doc-toc__toggle"
                        aria-expanded={tocOpen}
                        onClick={() => setTocOpen((open) => !open)}
                    >
                        <span>{tocTitle}</span>
                        <span className="markdown-doc-toc__chevron" aria-hidden="true">{tocOpen ? '▲' : '▼'}</span>
                    </button>
                    <div className="markdown-doc-toc__panel">
                        <p className="markdown-doc-toc__title">{tocTitle}</p>
                        <ol className="markdown-doc-toc__list">
                            {toc.map((item) => (
                                <li
                                    key={item.id}
                                    className={
                                        'markdown-doc-toc__item markdown-doc-toc__item--h' + item.level
                                        + (activeId === item.id ? ' is-active' : '')
                                    }
                                >
                                    <a
                                        href={'#' + item.id}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            scrollToHeading(item.id)
                                        }}
                                    >
                                        {item.text}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </div>
                </nav>
            )}
        </div>
    )
}
